/**
 * LoadoutStore — where a Loadout comes from and where changes go.
 *
 * One request, GET /api/locker → { owned, loadout|null }:
 *   loadout non-null  → signed in: seed from the account row, clear the guest
 *                       copy (account wins), persist each change with
 *                       PUT /api/locker/loadout.
 *   loadout null      → guest (or OAuth not configured): seed from
 *                       localStorage, write it back on every change.
 *   request fails     → fail closed: only items everyone owns count (isDefault,
 *                       or no unlock block — e.g. the 'none' backpack, as the
 *                       server's alwaysOwnedIds), status 'failed'; the guest
 *                       loadout still seeds so the join payload carries the
 *                       player's choice.
 * Match-end unlocks (`cosmeticsUnlocked`) call grant() so the locker sees new
 * ids without waiting for another GET.
 *
 * Everything is injected (fetchFn, storage) so this runs in Node.
 */
import { SLOT_KEYS } from './Loadout.js';

export const LOADOUT_STORAGE_KEY = 'driftshot.loadout';

export function createLoadoutStore({ loadout, fetchFn, storage, storageKey = LOADOUT_STORAGE_KEY, onError = () => {}, onPersistError = onError }) {
  let owned = defaultsOnly();
  const store = {
    status: 'idle',
    signedIn: false,

    async load() {
      let body = null;
      try {
        const res = await fetchFn('/api/locker');
        if (!res || !res.ok) throw new Error(`GET /api/locker → ${res?.status}`);
        body = await res.json();
        owned = normaliseOwned(body.owned);
        store.status = 'ok';
      } catch (e) {
        owned = defaultsOnly();
        store.status = 'failed';
        onError(e);
      }

      if (body?.loadout) {
        store.signedIn = true;
        loadout.set({
          skin: body.loadout.equippedSkin,
          trail: body.loadout.equippedTrail,
          backpack: body.loadout.equippedBackpack
        });
        safeRemove();
      } else {
        // A failed GET for a signed-in player leaves signedIn=false for this session: equips go to
        // localStorage and are discarded by the next successful load (account wins, spec D4).
        // Retry in the locker re-runs load().
        store.signedIn = false;
        loadout.set(readStorage());
      }
    },

    owns(slot, id) {
      return Array.isArray(owned[slot]) && owned[slot].includes(id);
    },

    ownedIds(slot) {
      return [...(owned[slot] || [])];
    },

    /**
     * Apply items just granted at match end (`cosmeticsUnlocked`) so the locker
     * reflects them without waiting for the next GET /api/locker.
     */
    grant(items) {
      if (!Array.isArray(items)) return;
      for (const item of items) {
        if (!item || !loadout.isKnown(item.slot, item.id)) continue;
        if (!Array.isArray(owned[item.slot])) owned[item.slot] = [loadout.defaultId(item.slot)];
        if (!owned[item.slot].includes(item.id)) owned[item.slot].push(item.id);
      }
    }
  };

  // Account PUTs are chained so two quick equips land in the order they happened
  // (persistToAccount never rejects — it catches internally).
  let persistQueue = Promise.resolve();

  loadout.subscribe((state) => {
    if (store.signedIn) {
      persistQueue = persistQueue.then(() => persistToAccount(state));
    } else {
      safeWrite(state);
    }
  });

  return store;

  function defaultsOnly() {
    const out = {};
    for (const slot of Object.keys(SLOT_KEYS)) out[slot] = alwaysOwned(slot);
    return out;
  }

  function alwaysOwned(slot) {
    const ids = loadout.entries(slot).filter((e) => e.isDefault === true || !e.unlock).map((e) => e.id);
    return ids.length ? ids : [loadout.defaultId(slot)];
  }

  function normaliseOwned(raw) {
    const out = {};
    for (const slot of Object.keys(SLOT_KEYS)) {
      const ids = Array.isArray(raw?.[slot]) ? raw[slot].filter((id) => loadout.isKnown(slot, id)) : [];
      for (const id of alwaysOwned(slot)) if (!ids.includes(id)) ids.push(id);
      out[slot] = ids;
    }
    return out;
  }

  function readStorage() {
    try {
      const raw = storage?.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  function safeWrite(state) {
    try { storage?.setItem(storageKey, JSON.stringify(state)); } catch { /* private mode / quota: the join payload still carries it */ }
  }

  function safeRemove() {
    try { storage?.removeItem(storageKey); } catch { /* ignore */ }
  }

  async function persistToAccount(state) {
    try {
      const res = await fetchFn('/api/locker/loadout', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ skinId: state.skin, trailId: state.trail, backpackId: state.backpack })
      });
      if (!res || !res.ok) throw new Error(`PUT /api/locker/loadout → ${res?.status}`);
    } catch (e) {
      onPersistError(e);
    }
  }
}
